import { IContentAssociation, IContentDocument } from '@engineering11/content-web'
import { APPLICATION_STATE, IEmployerVideoContent, IJobNotificationSettings, JobPostIntegrations, RequiredApplicationDetailKeys } from 'shared-lib'
import { IJobProfile, IJobProfileContentItem } from './job-profile.model'

export const CONTENT_TYPE_JOB_POST = 'job-post'

type Modify<T, R> = Omit<T, keyof R> & R

export enum JobPostStatus {
  Published = 'Published',
  Draft = 'Draft',
  ClosedFilled = 'ClosedFilled',
  ClosedNotFilled = 'ClosedNotFilled',
  Deleted = 'Deleted',
}

export const JOB_CLOSED_STATUSES = [JobPostStatus.ClosedFilled, JobPostStatus.ClosedNotFilled]

export enum JobPostVisibility {
  Public = 'public',
  /**
   * Not offered in the create flow yet. Confidential means "only the decision
   * team may SEE this opening", which is row-level access control across the
   * openings list, Algolia and notifications - none of which exists. The value
   * stays so the model is forward compatible and stored documents keep meaning.
   */
  Confidential = 'confidential',
  Internal = 'internal',
}

export enum JobPostContext {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
}

export enum JobPostAutomationStatus {
  NotConfigured = 'NotConfigured',
  Enabled = 'Enabled',
  Disabled = 'Disabled',
}

export interface IJobCloseSchedule {
  message: string
  senderId: string
  closeDate: Date
  messageDuration: Duration
  closeTaskId?: string
  messageTaskId?: string
}

export interface IJobPost extends Modify<IJobProfile, { status: JobPostStatus }> {
  sendToGoogle?: boolean
  hideFromCareerSite?: boolean
  visibility?: JobPostVisibility
  editorIds?: string[]
  integrations?: Array<JobPostIntegrations>
  deactivated?: Date
  video?: IEmployerVideoContent | null
  automationStatus?: JobPostAutomationStatus
  jobPaused?: boolean
  jobCloseSchedule?: IJobCloseSchedule
  requiredFields?: RequiredApplicationDetailKeys[]
  jobNotificationSettings?: IJobNotificationSettings
}

/**
 * Create-time defaults implied by a visibility choice. Public openings keep whatever
 * careers-page preference they already had; anything else is forced off it, and the
 * switch is not offered - see job-form-options-title-sub-form.
 */
export function visibilityCreateDefaults(visibility: JobPostVisibility): Partial<IJobPost> {
  return visibility === JobPostVisibility.Public ? { visibility } : { visibility, hideFromCareerSite: true }
}

/**
 * Applicant count per pipeline stage, maintained on the job post record by the backend and indexed into
 * the job post search index — so the openings list can draw a stage bar straight off a hit instead of one
 * aggregation query per (job, stage) (CNCT-6801).
 */
export type ApplicantsByStage = Record<APPLICATION_STATE, number>

export interface IPublishedJobPost extends IJobPost {
  totalApplicants?: number
  inactiveApplicants?: number
  unresponsiveApplicants?: number
  /** Absent on job posts the backend has not written or backfilled since CNCT-6801 shipped. */
  applicantsByStage?: ApplicantsByStage
}

export interface IJobPostContentAssociation extends IContentAssociation {
  swimlanes?: APPLICATION_STATE[]
}

export type IJobPostContentItem = Modify<
  IJobProfileContentItem,
  {
    document: IJobPost
    associations: Map<string, IJobPostContentAssociation>
  }
>

export function isJobPost(document: IContentDocument): document is IJobPost {
  return document.contentType === CONTENT_TYPE_JOB_POST
}

export const jobPostStatusMappings: Record<JobPostStatus, string> = {
  [JobPostStatus.Draft]: 'Draft',
  [JobPostStatus.Published]: 'Published',
  [JobPostStatus.ClosedFilled]: 'Closed: Filled',
  [JobPostStatus.ClosedNotFilled]: 'Closed: Not Filled',
  [JobPostStatus.Deleted]: 'Paused',
}
